import React, { useCallback } from 'react'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import { TtSpinner } from '@23/ui-react'

import { useRole } from '../../../global/blocs/roomInfo'
import {
  useFloorState,
  useIsParticipantCameraInFloor,
  useIsParticipantMobileInFloor,
  useIsParticipantScreenInFloor,
} from '../../../firebase/hooks/useFloor'
import { type Identifier } from '../../../firebase/model/floor-model'
import { useFullParticipantInfo } from '../../../webrtc/webRTCRoom'
import { VideoTrack } from '../../../MediaTracks/components/VideoTrack/VideoTrack'
import { IconComponent } from '../../../sharedLegacy/components'
import { QualityIndicatorComponent } from '../../../RoomUI/components/QualityIndicatorComponent/QualityIndicatorComponent'
import { useIsExternalFeed } from '../../../Speakers/blocs/useSpeakers'

import { ParticipantMutingTriggers } from './ParticipantMutingTriggers/ParticipantMutingTriggers'

import './Participant.scss'

const ExternalFeedWaitingMessage = () => (
  <div className="external-feed-waiting-message">
    <TtSpinner size="small" />
    <FormattedMessage defaultMessage="Waiting for external feed..." />
  </div>
)

const CameraMutedMessage = () => (
  <div className="camera-muted-message">
    <IconComponent
      name="camera-off"
      size={[16, 16]}
      color="FFFFFF"
      className="muted-state-icon"
    />

    <FormattedMessage defaultMessage="Webcam is off" />
  </div>
)

interface ParticipantProps {
  participantId: number
  onSoloClick: (participantId: number, identifier: Identifier) => void
  onMixedModeClick: (participantId: number) => void
}

const Participant = ({
  participantId,
  onSoloClick,
  onMixedModeClick,
}: ParticipantProps) => {
  const isExternalFeedSpeaker = useIsExternalFeed(participantId)
  const participant = useFullParticipantInfo(participantId)
  const floor = useFloorState()
  const role = useRole()
  const isParticipantCameraInFloor =
    useIsParticipantCameraInFloor(participantId)
  const isParticipantScreenInFloor =
    useIsParticipantScreenInFloor(participantId)
  const isParticipantMobileInFloor =
    useIsParticipantMobileInFloor(participantId)

  const handleCameraClick = useCallback(() => {
    onSoloClick(participantId, 'camera')
  }, [participantId, onSoloClick])

  const handleScreenshareClick = useCallback(() => {
    onSoloClick(participantId, 'screenshare')
  }, [participantId, onSoloClick])

  const handleMobileClick = useCallback(() => {
    onSoloClick(participantId, 'mobile')
  }, [participantId, onSoloClick])

  const handleMixedModeClick = useCallback(() => {
    onMixedModeClick(participantId)
  }, [participantId, onMixedModeClick])

  if (!participant) return null

  const { isLocal } = participant

  const isCameraEnabled = !!participant.tracks.camera?.isEnabled

  return (
    <div
      className={classnames('participant', {
        'is-external-feed-speaker': isExternalFeedSpeaker,
      })}
    >
      <div
        className="selectable-container participant-main-grid"
        data-selected={isParticipantCameraInFloor && floor?.layout === 'solo'}
      >
        <button
          type="button"
          className="primary-camera-container"
          data-clickable={role === 'producer'}
          onClick={handleCameraClick}
          aria-label={
            isParticipantCameraInFloor && !isParticipantScreenInFloor
              ? intl.formatMessage(
                  {
                    defaultMessage: "{name} 's camera is selected.",
                  },
                  { name: participant.name }
                )
              : intl.formatMessage(
                  {
                    defaultMessage: 'Press enter or space to show {name}',
                  },
                  { name: participant.name }
                )
          }
        >
          {isCameraEnabled && participant.tracks.camera ? (
            isExternalFeedSpeaker ? (
              <div className="external-feed-camera-feed">
                <VideoTrack
                  track={participant.tracks.camera}
                  objectFit="cover"
                />
              </div>
            ) : (
              <VideoTrack track={participant.tracks.camera} objectFit="cover" />
            )
          ) : isExternalFeedSpeaker ? (
            <ExternalFeedWaitingMessage />
          ) : (
            <CameraMutedMessage />
          )}
        </button>
        <div className="primary-info-container">
          <div className="participant-nameplate">
            {participant.role === 'producer' && (
              <span className="transparent-title-text">
                <FormattedMessage defaultMessage="Host" />
              </span>
            )}
            <span className="participant-name-text">{participant.name}</span>
          </div>
        </div>
        <div className="participant-controls">
          <ParticipantMutingTriggers
            isProducer={role === 'producer'}
            participantId={participantId}
          />
          {!isLocal && (
            <div className="quality-indicator-container">
              <QualityIndicatorComponent
                participantId={participantId}
                reason={intl.formatMessage({
                  defaultMessage:
                    'A speaker is currently experiencing issues with their connection',
                })}
              />
            </div>
          )}
        </div>
      </div>
      <div className="participant-auxiliary-grid">
        {participant.tracks.screen && (isLocal || role === 'producer') && (
          <button
            type="button"
            className="selectable-container"
            data-selected={
              isParticipantScreenInFloor && !isParticipantCameraInFloor
            }
            data-clickable={role === 'producer'}
            onClick={handleScreenshareClick}
            onKeyDown={handleScreenshareClick}
            aria-label={
              isParticipantScreenInFloor && !isParticipantCameraInFloor
                ? intl.formatMessage(
                    {
                      defaultMessage: 'The screen of {name} is selected.',
                    },
                    { name: participant.name }
                  )
                : intl.formatMessage(
                    {
                      defaultMessage:
                        'Press enter or space to show the shared screen OF {name}',
                    },
                    { name: participant.name }
                  )
            }
          >
            <VideoTrack track={participant.tracks.screen} objectFit="cover" />
          </button>
        )}
        {participant.tracks.mobile && (isLocal || role === 'producer') && (
          <button
            type="button"
            className="selectable-container"
            data-selected={isParticipantMobileInFloor}
            data-clickable={role === 'producer'}
            onClick={handleMobileClick}
          >
            <VideoTrack track={participant.tracks.mobile} objectFit="cover" />
          </button>
        )}
        {participant.tracks.camera?.isEnabled &&
          participant.tracks.screen &&
          role === 'producer' && (
            <button
              type="button"
              className="selectable-container"
              data-selected={
                isParticipantCameraInFloor &&
                isParticipantScreenInFloor &&
                floor?.layout !== 'panel'
              }
              data-clickable
              onClick={handleMixedModeClick}
              onKeyDown={handleMixedModeClick}
              aria-label={
                isParticipantCameraInFloor && isParticipantScreenInFloor
                  ? intl.formatMessage(
                      {
                        defaultMessage:
                          'The shared screen of and camera of {name} is selected.',
                      },
                      { name: participant.name }
                    )
                  : intl.formatMessage(
                      {
                        defaultMessage:
                          'Press enter or space to show the shared screen and camera of {name}',
                      },
                      { name: participant.name }
                    )
              }
            >
              <div className="mixed-mode-enclosure">
                <div className="mixed-mode-icon">
                  <IconComponent
                    name="mixed-mode-icon"
                    color="FFF"
                    size={[30, 17]}
                  />
                </div>
                <div className="mixed-mode-panel">
                  <FormattedMessage defaultMessage="Mixed" />
                </div>
              </div>
            </button>
          )}
      </div>
    </div>
  )
}

// eslint-disable-next-line import-x/no-default-export
export default Participant
